import { Link } from "react-router-dom"
import { useState, useEffect } from "react"
import {
  Calendar,
  Phone,
  MapPin,
  ChevronRight,
  MessageSquare,
  History,
  Target,
  Award,
  Users,
  Heart,
  Clock,
  Shield,
  Sparkles,
  CheckCircle,
  ArrowRight,
  Mail,
  Stethoscope,
  Zap,
  HeartPulse,
  Baby,
  Brain,
  Bone,
  User,
} from "lucide-react"
import SEO from "./SEO"
 
import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Badge } from "../components/ui/badge"
import api from "../utils/api"
 
export default function AboutTest() {
  const aboutSchema = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "https://pranaamhospitals.com/about",
    "name": "About Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/about",
    "description": "Learn about Pranaam Hospitals, a trusted multi-specialty healthcare institution in Hyderabad committed to excellence, ethics, and compassionate patient care.",
    "mainEntity": {
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaamhospitals.com/"
    }
  },
 
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals delivers world-class healthcare with compassion, innovation, and integrity across Hyderabad and surrounding regions.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ]
  }
];
 
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
 
  useEffect(() => {
    // Fetch dynamic content from WordPress
    fetch("/wp-json/wp/v2/pages?slug=about")
      .then((res) => res.json())
      .then((data) => {
        if (data && data[0]) {
          setPageData(data[0]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);
 
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", message: "" });
 
    try {
      // Sends the form data to your WP REST API which should be configured with WP SMTP
      const response = await api.post("/wp-json/custom/v1/send-email", formData);
      if (response.data.success) {
        setFormStatus({ type: "success", message: "Message sent successfully!" });
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setFormStatus({ type: "error", message: response.data.message || "Failed to send message." });
      }
    } catch (error) {
      setFormStatus({ type: "error", message: "An error occurred. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };
 
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
 
  const acf = pageData?.acf || {};
  const seo = pageData?.seo || {};
 
  return (
     <>
        <SEO
      title={seo.title || "About Pranaam Hospitals | Trusted Healthcare in Hyderabad"}
      description={seo.description || "Learn about Pranaam Hospitals' vision, mission, and commitment to compassionate healthcare excellence."}
      canonical={seo.canonical || "https://pranaamhospitals.com/about"}
      robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
      keywords={acf.key_words || ""}
      ogTitle={seo.og_title}
      ogDescription={seo.og_description}
      ogImage={seo.og_image || seo.featured_image}
      twitterTitle={seo.twitter_title}
      twitterDescription={seo.twitter_description}
      author="Pranaam Hospitals"
      publisher="Pranaam Hospitals"
      schema={aboutSchema}
    />
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="container px-4 mx-auto">
            <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
              <div className="max-w-lg">
                <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                  {acf.about_test_hero_badge}
                </Badge>
                <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                  {acf.about_test_hero_title}
                </h1>
                <p className="mb-8 text-lg text-gray-700">
                  {acf.about_test_hero_description}
                </p>
                <div className="flex flex-wrap gap-4">
                   <Button size="lg" asChild>
                      <a
                        href={acf.about_test_appointment_link || "/book-now"}
                        rel="noopener noreferrer"
                      >
                        <Calendar className="mr-2 h-5 w-5" />
                        {acf.about_test_hero_btn1_text || "Book an Appointment"}
                      </a>
                    </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 border-primary text-primary hover:bg-primary hover:text-white font-heading"
                    asChild
                  >
                    <a href={acf.about_test_hero_btn2_link || "tel:+919966060000"}>
                      <Phone className="w-5 h-5" />
                      {acf.about_test_hero_btn2_text || "Call Us"}
                    </a>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="relative h-50 overflow-hidden rounded-lg shadow-xl md:h-[385px]">
                  <img
                    src={acf.about_test_hero_image}
                    alt={acf.about_test_hero_title}
                    className="w-full h-full object-contain"
                  />
                 
                </div>
                {/* Material Design decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-full"></div>
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/10 rounded-full"></div>
              </div>
            </div>
          </div>
        </section>
 
        {/* Who We Are Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
                <Sparkles className="w-5 h-5" />
                <span>{acf.about_test_who_badge}</span>
              </div>
              <h2 className="mb-6 text-3xl font-bold md:text-4xl">
                {acf.about_test_who_title}
              </h2>
              <p className="text-lg text-gray-700">
                {acf.about_test_who_description}
              </p>
            </div>
 
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {acf.about_test_who_cards?.map((card, index) => {
                // Dynamically resolve the icon component based on the string name from ACF
                const IconComponent = { HeartPulse, Bone, Brain, Baby, User, Stethoscope, Zap, Clock }[card.icon] || Stethoscope;
                return (
                  <Card key={index} className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-full bg-primary/10">
                          <IconComponent className="w-8 h-8 text-primary" />
                        </div>
                      </div>
                      <h3 className="mb-2 text-lg font-bold">{card.title}</h3>
                      <p className="text-sm text-gray-600">
                        {card.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
 
            <div className="max-w-3xl mx-auto mt-12 text-center">
              <p className="text-lg text-gray-700">
                Our focus is on early diagnosis, personalized treatment, and continuous care—ensuring the best outcomes
                for our patients.
              </p>
            </div>
          </div>
        </section>
 
        {/* Mission, Vision, Values Tabs */}
        <section className="py-16 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto">
              <Tabs defaultValue="vision" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="vision">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Our Vision
                  </TabsTrigger>
                  <TabsTrigger value="mission">
                    <Target className="w-4 h-4 mr-2" />
                    Our Mission
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="vision" className="p-6 border rounded-lg shadow-sm bg-white">
                  <h2 className="mb-4 text-2xl font-bold text-center">{acf.about_test_vision_title || "Our Vision"}</h2>
                  <p className="mb-6 text-center text-gray-700">
                    {acf.about_test_vision_description}
                  </p>
                  <div className="p-4 mb-4 bg-primary/5 rounded-lg">
                    <p className="italic text-gray-700">
                      "{acf.about_test_vision_quote}"
                    </p>
                  </div>
                </TabsContent>
                <TabsContent value="mission" className="p-6 border rounded-lg shadow-sm bg-white">
                  <h2 className="mb-4 text-2xl font-bold text-center">{acf.about_test_mission_title || "Our Mission"}</h2>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {acf.about_test_mission_list?.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-primary/10">
                          <CheckCircle className="w-5 h-5 text-primary" />
                        </div>
                        <div><p className="text-gray-700">{item.item_text}</p></div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>
 
        {/* Contact CTA */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <Card className="overflow-hidden shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="relative pt-28 md:pt-0">
                  <img
                    src={acf.about_test_cta_image || "/images/PranaamHospitalsAwards.png"}
                    alt="Contact Pranaam Hospitals"
                    className="w-full h-full object-cover opacity-70"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/40 flex items-center justify-center">
                    <div className="p-8 text-white">
                      <h3 className="mb-4 text-3xl font-bold">Get in Touch</h3>
                      <p className="mb-6">
                        Whether you're seeking a consultation, second opinion, or emergency care—we're here for you.
                      </p>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 mt-1 shrink-0" />
                          <div>
                            <p className="font-medium">Emergency Number</p>
                            <p>{acf.about_test_emergency_phone || "+91 98485 98285"}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 mt-1 shrink-0" />
                          <div>
                            <p className="font-medium">Helpline</p>
                            <p>{acf.about_test_helpline_phone || "+91 99660 60000"}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Mail className="w-5 h-5 mt-1 shrink-0" />
                          <div>
                            <p className="font-medium">Email</p>
                            <p>{acf.about_test_email || "info@pranaamhospitals.in"}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Calendar className="w-5 h-5 mt-1 shrink-0" />
                          <div>
                            <p className="font-medium">Appointments</p>
                            <p>{acf.about_test_appointments_text || "040 230 42777"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
 
               {/* Contact CTA */}
                <div className="p-8">
                  <h3 className="mb-6 text-2xl font-bold">Send Us a Message</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block mb-2 text-sm font-medium">
                          Your Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block mb-2 text-sm font-medium">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="subject" className="block mb-2 text-sm font-medium">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="How can we help you?"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block mb-2 text-sm font-medium">
                        Message
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="Your message here..."
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      ></textarea>
                    </div>
                    {formStatus.message && (
                      <div className={`p-3 rounded-md text-sm ${formStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {formStatus.message}
                      </div>
                    )}
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? "Sending..." : "Send Message"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </div>
               
              </div>
            </Card>
          </div>
        </section>
      </main>
    </div>
    </>
  )
}
